import React, { useState, useEffect } from 'react';
import {
  Plus,
  Save,
  Trash2,
  Edit3,
  X,
  Loader2,
  Building2,
} from 'lucide-react';
import {
  getProveedores,
  createProveedor,
  updateProveedor,
  deleteProveedor,
} from '../services/api';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  nombre: '',
  abreviatura: '',
  cif: '',
  email: '',
};

export default function Proveedores() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = async () => {
    try {
      const data = await getProveedores();
      setProveedores(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Error cargando proveedores: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      await createProveedor(form);
      toast.success('Proveedor creado');
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error('Error creando proveedor: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (prov) => {
    setEditingId(prov.id);
    setEditForm({
      nombre: prov.nombre || '',
      abreviatura: prov.abreviatura || '',
      cif: prov.cif || '',
      email: prov.email || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleUpdate = async (id) => {
    if (!editForm.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      await updateProveedor(id, editForm);
      toast.success('Proveedor actualizado');
      setEditingId(null);
      fetchData();
    } catch (err) {
      toast.error('Error actualizando: ' + err.message);
    }
  };

  const handleDelete = async (prov) => {
    const count = prov.factura_count || prov.num_facturas || 0;
    if (count > 0) {
      toast.error(`No se puede eliminar: tiene ${count} factura${count !== 1 ? 's' : ''} asociada${count !== 1 ? 's' : ''}`);
      return;
    }
    if (!window.confirm(`¿Eliminar el proveedor "${prov.nombre}"?`)) return;
    try {
      await deleteProveedor(prov.id);
      toast.success('Proveedor eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error eliminando: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Proveedores</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Nuevo Proveedor
        </button>
      </div>

      {/* New provider form */}
      {showForm && (
        <form onSubmit={handleCreate} className="card">
          <h2 className="text-sm font-semibold text-gray-500 uppercase mb-4">
            Nuevo Proveedor
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="input-field text-sm"
                placeholder="Nombre del proveedor"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Abreviatura
              </label>
              <input
                type="text"
                value={form.abreviatura}
                onChange={(e) => setForm({ ...form, abreviatura: e.target.value })}
                className="input-field text-sm"
                placeholder="Abrev."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">CIF</label>
              <input
                type="text"
                value={form.cif}
                onChange={(e) => setForm({ ...form, cif: e.target.value })}
                className="input-field text-sm"
                placeholder="B12345678"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-field text-sm"
                placeholder="email@ejemplo.com"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm({ ...EMPTY_FORM });
              }}
              className="btn-secondary text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-1 text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                Nombre
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                Abreviatura
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                CIF
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                Email
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                Facturas
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {proveedores.map((prov) => {
              const isEditing = editingId === prov.id;
              return (
                <tr key={prov.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.nombre}
                        onChange={(e) =>
                          setEditForm({ ...editForm, nombre: e.target.value })
                        }
                        className="input-field text-sm"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {prov.nombre}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.abreviatura}
                        onChange={(e) =>
                          setEditForm({ ...editForm, abreviatura: e.target.value })
                        }
                        className="input-field text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-600">
                        {prov.abreviatura || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.cif}
                        onChange={(e) =>
                          setEditForm({ ...editForm, cif: e.target.value })
                        }
                        className="input-field text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-600 font-mono">
                        {prov.cif || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) =>
                          setEditForm({ ...editForm, email: e.target.value })
                        }
                        className="input-field text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-600">{prov.email || '-'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-gray-600">
                      {prov.factura_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleUpdate(prov.id)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Guardar"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                            title="Cancelar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(prov)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                            title="Editar"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(prov)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {proveedores.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                  No hay proveedores registrados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
